/**
 * featureOrCoordToLngLat — discriminate a Mapbox Search Box selection into a
 * validated [lng, lat] pair, or null when not locatable.
 *
 * MapboxSearch's `onSelect` emits either a feature suggestion or a parsed
 * coordinate pair. This is the single source of truth for that branching
 * and its boundary guards (Point-only geometry, finite values, WGS-84 range),
 * shared by MapSearchBar and DashboardPage so the two cannot drift.
 */
import type { LngLatLike } from 'mapbox-gl';
import type { SearchBoxFeatureSuggestion } from '@/types/mapbox';

/** Valid longitude range (WGS-84). */
const MAX_LNG = 180;

/** Valid latitude range (WGS-84). */
const MAX_LAT = 90;

function inWgs84Range(lng: number, lat: number): boolean {
  return (
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    lng >= -MAX_LNG &&
    lng <= MAX_LNG &&
    lat >= -MAX_LAT &&
    lat <= MAX_LAT
  );
}

export function featureOrCoordToLngLat(
  value: SearchBoxFeatureSuggestion | LngLatLike,
): [number, number] | null {
  if (value == null) {
    return null;
  }

  // Feature suggestion branch: only a Point carries a usable [lng, lat];
  // any other geometry the Search Box API might return is not locatable.
  if (typeof value === 'object' && 'geometry' in value) {
    const { geometry } = value;
    if (geometry.type !== 'Point') {
      return null;
    }
    // GeoJSON Point coordinates are [lng, lat].
    const [lng, lat] = geometry.coordinates;
    return inWgs84Range(lng, lat) ? [lng, lat] : null;
  }

  // Coordinate-pair branch: a pasted [lng, lat] MapboxSearch has already
  // parsed. The finite / length / WGS-84 checks are a DEFENSIVE boundary guard
  // (depth in case that upstream contract changes), not the user-facing path.
  if (Array.isArray(value) && value.length === 2) {
    const [lng, lat] = value as [number, number];
    return inWgs84Range(lng, lat) ? [lng, lat] : null;
  }

  return null;
}
